// frontend/src/utils/mockData.ts
// 开发环境 Mock 用户配置

import type { UserInfo } from '@/types/api'

type MockRole =
  | 'super_admin'
  | 'system_admin'
  | 'approval_admin'
  | 'menu_user'
  | 'rd_cloud'
  | 'rd_terminal'

export interface MockUserProfile {
  id: string
  name: string
  personId: string
  dingUserId: string
  unionId: string
  department: string
  empCode: string
  roles: MockRole[]
  accessUser?: string[]
  accessTelUser?: string[]
  accessMenuUser?: string[]
  selBox?: string[]
  selBoxKv?: { label: string; value: number }[]
}

const defaultSelBox = ['选项1', '选项2', '选项3']
const defaultSelBoxKv = [
  { label: '选项一', value: 1 },
  { label: '选项二', value: 2 },
  { label: '选项三', value: 3 }
]

/**
 * 维护这份人员列表即可。
 * 调试时只需要配置 id、姓名、personId 或钉钉 userId 中任意一个。
 */
export const mockUserProfiles: MockUserProfile[] = [
  {
    id: 'dev-user',
    name: '开发测试用户',
    personId: 'mock_user_person_001',
    dingUserId: 'mock_user_id_001',
    unionId: 'mock_user_union_id',
    department: '开发部',
    empCode: 'DEV001',
    roles: [],
    accessUser: ['开发测试用户'],
    accessTelUser: ['开发测试用户']
  },
  {
    id: 'jin-kun',
    name: '金坤',
    personId: '04562912621179571',
    dingUserId: '04562912621179571',
    unionId: '04562912621179571',
    department: '研发一部',
    empCode: 'ADMIN001',
    roles: ['super_admin'],
    accessUser: ['金坤', '冯盛', '钱佳绮'],
    accessTelUser: ['金坤', '冯盛', '王汉振', '李强', '仲雪君'],
    selBox: ['管理选项1', '管理选项2'],
    selBoxKv: [
      { label: '管理选项一', value: 1 },
      { label: '管理选项二', value: 2 }
    ]
  },
  {
    id: 'wang-hanzhen',
    name: '王汉振',
    personId: 'mock_menu_person_001',
    dingUserId: 'mock_menu_id_001',
    unionId: 'mock_menu_union_id',
    department: '开发部',
    empCode: 'MENU001',
    roles: ['menu_user'],
    accessTelUser: ['王汉振', '李强', '仲雪君'],
    accessMenuUser: ['王汉振', '李强', '仲雪君']
  },
  {
    id: 'xu-yan',
    name: '徐言',
    personId: '1776144488196607',
    dingUserId: '1776144488196607',
    unionId: '1776144488196607',
    department: '研发一部',
    empCode: 'SR-WX422',
    roles: []
  }
]

export const mockUserOptions = mockUserProfiles.map((profile) => ({
  id: profile.id,
  name: `${profile.name}${profile.roles.includes('super_admin') ? '(超级管理员)' : ''}`,
  data: profile
}))

function normalizeValue(value?: string | null): string {
  return (value || '').trim().toLowerCase()
}

function buildUserInfo(profile: MockUserProfile, overrides?: {
  name?: string | null
  department?: string | null
  empCode?: string | null
}): UserInfo {
  const name = overrides?.name?.trim() || profile.name
  const department = overrides?.department?.trim() || profile.department
  const empCode = overrides?.empCode?.trim() || profile.empCode

  return {
    ding: {
      nick: name,
      userId: profile.dingUserId,
      unionId: profile.unionId
    },
    person_id: profile.personId,
    udm_person: {
      name,
      department,
      emp_code: empCode
    },
    roles: [...profile.roles],
    is_super_admin: profile.roles.includes('super_admin'),
    is_system_admin: profile.roles.includes('system_admin'),
    is_approval_admin: profile.roles.includes('approval_admin'),
    is_rd: profile.roles.includes('rd_cloud') || profile.roles.includes('rd_terminal'),
    rd_type: profile.roles.includes('rd_cloud')
      ? 'cloud'
      : profile.roles.includes('rd_terminal')
        ? 'terminal'
        : null,
    access_user: profile.accessUser || [],
    access_tel_user: profile.accessTelUser || [],
    access_menu_user: profile.accessMenuUser || [],
    sel_box: profile.selBox || defaultSelBox,
    sel_box_kv: profile.selBoxKv || defaultSelBoxKv
  }
}

function findMockProfile(key?: string | null): MockUserProfile | undefined {
  const normalizedKey = normalizeValue(key)
  if (!normalizedKey) {
    return undefined
  }

  return mockUserProfiles.find((profile) => {
    const candidates = [
      profile.id,
      profile.name,
      profile.personId,
      profile.dingUserId,
      profile.empCode
    ]

    return candidates.some((candidate) => normalizeValue(candidate) === normalizedKey)
  })
}

function getFallbackProfile(): MockUserProfile {
  return mockUserProfiles[0]
}

/**
 * 获取当前环境是否是开发模式
 */
export function isDevMode(): boolean {
  return import.meta.env.DEV === true ||
    import.meta.env.VITE_DEV_MODE === 'true' ||
    import.meta.env.MODE === 'development'
}

/**
 * 获取当前环境是否是 Mock 模式
 */
export function isMockMode(): boolean {
  const configuredMockUserId = normalizeValue(import.meta.env.VITE_MOCK_USER_ID)
  return configuredMockUserId.length > 0
}

/**
 * 获取 Mock 用户数据
 * `userKey` 支持 profile.id / 姓名 / personId / 钉钉 userId / 工号
 */
export function getMockUserData(userKey?: string): UserInfo {
  return buildUserInfo(findMockProfile(userKey) || getFallbackProfile())
}

/**
 * 设置 Mock 用户信息
 * 推荐只配置 `userId` 或 `userName` 其中一个即可。
 */
export function setMockUserConfig(config: {
  userId?: string
  userName?: string
  department?: string
  empCode?: string
  isAdmin?: boolean
}) {
  localStorage.setItem('ENABLE_MOCK_USER', 'true')

  if (config.userId) {
    localStorage.setItem('MOCK_USER_ID', config.userId)
  } else {
    localStorage.removeItem('MOCK_USER_ID')
  }

  if (config.userName) {
    localStorage.setItem('MOCK_USER_NAME', config.userName)
  } else {
    localStorage.removeItem('MOCK_USER_NAME')
  }

  if (config.department) {
    localStorage.setItem('MOCK_USER_DEPT', config.department)
  } else {
    localStorage.removeItem('MOCK_USER_DEPT')
  }

  if (config.empCode) {
    localStorage.setItem('MOCK_USER_CODE', config.empCode)
  } else {
    localStorage.removeItem('MOCK_USER_CODE')
  }

  if (config.isAdmin !== undefined) {
    localStorage.setItem('MOCK_USER_IS_ADMIN', String(config.isAdmin))
  } else {
    localStorage.removeItem('MOCK_USER_IS_ADMIN')
  }
}

/**
 * 清除 Mock 用户配置
 */
export function clearMockUserConfig() {
  localStorage.removeItem('ENABLE_MOCK_USER')
  localStorage.removeItem('MOCK_USER_ID')
  localStorage.removeItem('MOCK_USER_NAME')
  localStorage.removeItem('MOCK_USER_DEPT')
  localStorage.removeItem('MOCK_USER_CODE')
  localStorage.removeItem('MOCK_USER_IS_ADMIN')
}

/**
 * 获取完整的 Mock 用户信息（用于 auth store）
 */
export function getMockUserInfo(): UserInfo {
  const configuredId = import.meta.env.VITE_MOCK_USER_ID as string | undefined
  const profile = findMockProfile(configuredId) || getFallbackProfile()
  return buildUserInfo(profile)
}
